import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {SlideshowControls} from 'shells/carousel/slideshow-controls';
import {colors} from 'shells/colors/colors';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {RightFrame, ModalMain, FooterWithControls} from 'shells/modal/step-modal/layout';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {ModalHeader} from 'shells/typography/modal-header';
import {ModalSubheader} from 'shells/typography/modal-subheader';
import {ModalTitle} from 'shells/typography/modal-title';

import {
    getServiceTypeFromString,
    getActivityTypeSettingName,
    getServiceNoun,
} from '../../calls/helpers';
import {useGetSessionUser} from '../../core/use-get-session-user';

import {trackPhoneIntegrationOnboardCompleted} from './analytics-helpers';
import {CallActivityTypeSelect} from './call-activity-type-select';
import {useSyncPastCalls} from './graphql/hooks/use-sync-past-calls';

type Props = {
    goBack: () => void;
    nextPage: string;
    history: RouterHistory;
    shouldSyncPastCalls: boolean;
    search: string;
};

const SYNC_FROM_PAST_DAYS = 30;

export function ConfigureActivityTypes(props: Props) {
    const integrationTypeQueryParam =
        new URLSearchParams(props.search).get('integrationType') || '';
    const integrationType = getServiceTypeFromString(integrationTypeQueryParam);
    const isClickToCall = integrationType === 'CLICKTOCALL';

    const {user, isLoading: isLoadingUser, refetch} = useGetSessionUser();
    const phoneIntegration = user
        ? user.phoneIntegrations.find(
              (integration) => integration.integrationType === integrationType
          )
        : null;
    const clickToCallIntegration = user ? user.clickToCallIntegration : null;

    const integration = isClickToCall ? clickToCallIntegration : phoneIntegration;

    const {syncPastCalls, isLoading} = useSyncPastCalls();

    const handleNext = () => {
        if (integrationType) {
            trackPhoneIntegrationOnboardCompleted(integrationType);
        }

        refetch().then(() => {
            if (props.shouldSyncPastCalls && integrationType) {
                syncPastCalls({
                    syncPastCalls: {
                        integrationType,
                        initialSyncStartFromDaysAgo: SYNC_FROM_PAST_DAYS,
                    },
                })
                    .then(() => {
                        props.history.push(`${props.nextPage}`);
                    })
                    .catch(() => {
                        // TODO error handling and display
                    });
            } else {
                props.history.push(`${props.nextPage}`);
            }
        });
    };

    if (isLoadingUser) {
        return (
            <RightFrame>
                <LoadingPage size={64} fill={colors.blueLt} />
            </RightFrame>
        );
    }

    return (
        <RightFrame>
            <ModalMain fullheight={true}>
                <SpacerGroup mb={32}>
                    <ModalHeader>Last question</ModalHeader>
                    <ModalSubheader>
                        Make call logging faster by setting a default activity type that fits your
                        workflow.
                    </ModalSubheader>
                </SpacerGroup>
                {integration && (
                    <Stack spacing={16}>
                        <CallActivityTypeSelect
                            label={
                                <ModalTitle>
                                    Which activity type would you like{' '}
                                    {getServiceNoun(integration.integrationType)} calls to be logged
                                    as?
                                </ModalTitle>
                            }
                            activityTypeId={
                                integration.defaultCallsActivityType &&
                                integration.defaultCallsActivityType.id
                            }
                            settingName={
                                getActivityTypeSettingName(integration.integrationType, false) || ''
                            }
                            isHorizontal={false}
                            autoFocus={true}
                        />
                        {(integration.integrationType === 'RINGCENTRAL' ||
                            integration.integrationType === 'KIXIE') && (
                            <CallActivityTypeSelect
                                label={
                                    <ModalTitle>
                                        Which activity type would you like{' '}
                                        {getServiceNoun(integration.integrationType)} voicemails to
                                        be logged as?
                                    </ModalTitle>
                                }
                                activityTypeId={
                                    integration.defaultVoicemailsActivityType &&
                                    integration.defaultVoicemailsActivityType.id
                                }
                                settingName={
                                    getActivityTypeSettingName(integration.integrationType, true) ||
                                    ''
                                }
                                isHorizontal={false}
                                autoFocus={false}
                            />
                        )}
                    </Stack>
                )}
            </ModalMain>
            <FooterWithControls
                controls={
                    <SlideshowControls
                        selectedSlide={integrationType === 'CLICKTOCALL' ? 4 : 3}
                        slideCount={integrationType === 'CLICKTOCALL' ? 4 : 3}
                    />
                }
                buttons={
                    <React.Fragment>
                        <Button variant='text' onClick={props.goBack}>
                            Back
                        </Button>
                        <SaveButton
                            variant='primary'
                            size='big'
                            // @ts-expect-error
                            onClick={handleNext}
                            isSaving={isLoading}
                            buttonText='Finish'
                        />
                    </React.Fragment>
                }
            />
        </RightFrame>
    );
}
